import React, { useState } from "react";
import cn from "classnames";
import styles from "./ImagesAndCTA.module.sass";
import Card from "../../../../components/Card";
import File from "../../../../components/File";
import TextInput from "../../../../components/TextInput";
import Actions from "../../../../components/Actions";
import Icon from "../../../../components/Icon";
import { toast } from "react-toastify";
import { updateFormation } from "../../../../services/formation.service";

const ConventionInformations = ({ className, formation, onFormationChange, loading, visible = true, onSubmit }) => {
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  if (!visible) return null;

  // Fonction pour télécharger le document de convention
  const handleDownloadDocument = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Si c'est un document existant de la formation
    if (formation?.convention_model) {
      // Créer l'URL de téléchargement pour PocketBase
      const baseUrl = process.env.REACT_APP_POCKETBASE_URL || 'http://localhost:8090';
      const downloadUrl = `${baseUrl}/api/files/formations/${formation.id}/${formation.convention_model}`;
      
      // Créer un lien temporaire pour télécharger
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = formation.convention_model;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Téléchargement du document lancé');
    }
    // Si c'est un document nouvellement uploadé
    else if (uploadedDocument?.fileObject) {
      // Créer une URL temporaire pour le fichier
      const url = URL.createObjectURL(uploadedDocument.fileObject);
      const link = document.createElement('a');
      link.href = url;
      link.download = uploadedDocument.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Téléchargement du document lancé');
    }
  };

  // Fonction pour supprimer le document de convention
  const handleDeleteDocument = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (deleteLoading) return; // Empêcher les clics multiples
    
    try {
      setDeleteLoading(true);
      
      // Si c'est un document existant de la formation
      if (formation?.convention_model) {
        // Mettre à jour directement dans PocketBase
        await updateFormation(formation.id, {
          convention_model: null
        });
        
        // Mettre à jour l'état local
        if (onFormationChange) {
          onFormationChange({
            target: {
              name: 'convention_model',
              value: null
            }
          });
        }
        toast.success('Document de convention supprimé et sauvegardé');
      }
      // Si c'est un document nouvellement uploadé
      else if (uploadedDocument) {
        setUploadedDocument(null);
        if (onFormationChange) {
          onFormationChange({
            target: {
              name: 'convention_file',
              value: null
            }
          });
        }
        toast.success('Document supprimé avec succès');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression du document');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Fonction pour gérer l'upload du document
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    console.log('Fichier sélectionné:', file.name);
    
    try {
      setUploadLoading(true);
      
      // Simuler un délai d'upload pour voir le loading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Créer un objet document simulé (à adapter selon votre logique)
      const documentData = {
        id: Date.now(), // ID temporaire
        file: file.name,
        title: file.name,
        type: "Convention",
        created: new Date().toISOString(),
        size: file.size,
        fileObject: file // Garder le fichier pour un traitement ultérieur
      };
      
      console.log('Document créé:', documentData);
      setUploadedDocument(documentData);
      
      // Mettre à jour les données de formation avec le fichier
      if (onFormationChange) {
        onFormationChange({
          target: {
            name: 'convention_file',
            value: file
          }
        });
        console.log('Fichier ajouté aux données de formation:', file.name);
      }
      
      toast.success('Document importé avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      toast.error('Erreur lors de l\'import du document');
    } finally {
      setUploadLoading(false);
    }
  };

  // Actions pour le document existant
  const getDocumentActions = () => [
    {
      title: "Télécharger",
      action: (event) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        handleDownloadDocument(event);
      }
    },
    {
      title: deleteLoading ? "Suppression..." : "Supprimer",
      action: (event) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        if (!deleteLoading) {
          handleDeleteDocument(event);
        }
      }
    },
  ];



  return (
    <Card
      className={cn(styles.card, className)}
      title="Convention de formation"
      classTitle="title-blue"
    >
      <div className={styles.images}>
        {uploadLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingContent}>
              <div className={styles.spinner}></div>
              <p>Import du document en cours...</p>
            </div>
          </div>
        ) : formation?.convention_model || uploadedDocument ? (
          <div className={styles.uploadedDocument}>
            <div className={styles.documentHeader}>
              <h4>Document de convention</h4>
            </div>
            <div className={styles.documentItem}>
              <div className={styles.preview}>
                <img src={'/images/content/contract_logo.png'} alt="Document" />
              </div>
              <div className={styles.documentInfo}>
                <div className={styles.title}>
                  {formation?.convention_model ? (
                    formation.convention_model.length > 30 
                      ? formation.convention_model.substring(0, 30) + '...' 
                      : formation.convention_model
                  ) : (
                    uploadedDocument?.title.length > 30 
                      ? uploadedDocument.title.substring(0, 30) + '...' 
                      : uploadedDocument?.title
                  )}
                </div>
                <div className={styles.details}>
                  {formation?.convention_model ? 
                    'Convention - Attaché à la formation' : 
                    `${uploadedDocument?.type} - Importé le ${new Date(uploadedDocument?.created).toLocaleDateString('fr-FR')}`
                  }
                </div>
              </div>
              <div 
                className={styles.documentActions}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={getDocumentActions()}
                />
              </div>
            </div>
          </div>
        ) : (
          <File
            className={styles.field}
            title="Glissez-déposez ou cliquez pour importer la convention"
            label="Convention de formation"
            tooltip="Formats acceptés: PDF, DOC, DOCX"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx"
          />
        )}
      </div>

      <div className={styles.divider}></div>

      {/* Bouton de soumission */}
      <div className={styles.actions}>
        <button
          className={cn("button", styles.button)}
          type="submit"
          disabled={loading}
        >
          {loading ? "Enregistrement en cours..." : "Enregistrer les modifications"}
        </button>
      </div>
    </Card>
  );
};

export default ConventionInformations;
